// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { sendEmail } from '@/lib/email';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const w9Schema = z.object({
  legal_name: z.string().min(1, 'Legal name is required'),
  business_name: z.string().optional(),
  tax_id_type: z.enum(['ssn', 'ein']),
  tax_id: z.string().min(9, 'Tax ID must be at least 9 digits').max(11, 'Tax ID is too long'),
  tax_classification: z.enum([
    'individual',
    'sole_proprietor',
    'c_corp',
    's_corp',
    'partnership',
    'trust_estate',
    'llc_c',
    'llc_s',
    'llc_p',
    'other',
  ]),
  address_line1: z.string().min(1, 'Address is required'),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2, 'State is required').max(2, 'Use 2-letter state code'),
  zip_code: z.string().min(5, 'ZIP code must be at least 5 digits'),
  exempt_payee_code: z.string().optional(),
  fatca_exempt_code: z.string().optional(),
});

type W9FormData = z.infer<typeof w9Schema>;

export const W9Form: React.FC = () => {
  const { user, profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingW9, setExistingW9] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<W9FormData>({
    resolver: zodResolver(w9Schema),
  });

  // Fetch existing W-9 if available
  React.useEffect(() => {
    const fetchExistingW9 = async () => {
      if (!profile?.id) return;

      const { data, error } = await supabase
        .from('tax_forms')
        .select('*')
        .eq('profile_id', profile.id)
        .eq('form_type', 'w9')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setExistingW9(data);
        // Pre-fill form with existing data
        Object.keys(data).forEach((key) => {
          if (key in w9Schema.shape) {
            setValue(key as any, data[key]);
          }
        });
      }
    };

    fetchExistingW9();
  }, [profile?.id, setValue]);

  const onSubmit = async (data: W9FormData) => {
    if (!user || !profile) {
      toast.error('You must be logged in to submit W-9');
      return;
    }

    setIsSubmitting(true);

    try {
      // Save W-9 data to database
      const { data: savedW9, error: dbError } = await supabase
        .from('tax_forms')
        .upsert({
          user_id: user.id,
          profile_id: profile.id,
          form_type: 'w9',
          status: 'submitted',
          submitted_at: new Date().toISOString(),
          ...data,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Generate PDF using Supabase functions.invoke
      const { data: pdfData, error: pdfError } = await invokeEdgeFunction('generate-w9-pdf', {
        body: { formData: data },
      });

      if (pdfError) {
        console.error('PDF generation error:', pdfError);
        throw new Error(pdfError.message || 'Failed to generate PDF');
      }

      // Decode base64 PDF to binary
      const binaryString = atob(pdfData.pdf);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });

      // Upload PDF to Storage
      const fileName = `${user.id}/${Date.now()}-W9.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('w9-forms')
        .upload(fileName, blob, {
          contentType: 'application/pdf',
          upsert: false,
        });

      if (uploadError) {
        console.error('Failed to upload PDF:', uploadError);
        // Still download locally even if upload fails
      }

      // Get signed URL (valid for 7 days)
      let pdfUrl = '';
      if (!uploadError) {
        const { data: urlData } = await supabase.storage
          .from('w9-forms')
          .createSignedUrl(fileName, 604800); // 7 days in seconds
        
        pdfUrl = urlData?.signedUrl || '';
      }

      // Download the PDF locally
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfData.filename || `W9-${data.legal_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Send notification email to Mike with PDF link
      try {
        await sendEmail(
          'mike@recruitlook.com',
          `New W-9 Submission from ${data.legal_name}`,
          `A new W-9 form has been submitted.\n\nSubmitter: ${data.legal_name}\nBusiness Name: ${data.business_name || 'N/A'}\nEmail: ${profile.email || 'N/A'}\nRole: ${profile.role || 'N/A'}\nTax Classification: ${data.tax_classification}\nSubmitted: ${new Date().toLocaleString()}\n\n${pdfUrl ? `Download W-9 PDF: ${pdfUrl}` : ''}`,
          `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <div style="background: #002E62; padding: 20px; text-align: center;">
                <h1 style="color: white; margin: 0;">New W-9 Form Submission</h1>
              </div>
              <div style="padding: 20px; background: #f8f9fa;">
                <h2 style="color: #002E62;">Submission Details</h2>
                <table style="width: 100%; border-collapse: collapse;">
                  <tr style="border-bottom: 1px solid #ddd;">
                    <td style="padding: 10px; font-weight: bold;">Legal Name:</td>
                    <td style="padding: 10px;">${data.legal_name}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid #ddd;">
                    <td style="padding: 10px; font-weight: bold;">Business Name:</td>
                    <td style="padding: 10px;">${data.business_name || 'N/A'}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid #ddd;">
                    <td style="padding: 10px; font-weight: bold;">Email:</td>
                    <td style="padding: 10px;">${profile.email || 'N/A'}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid #ddd;">
                    <td style="padding: 10px; font-weight: bold;">Role:</td>
                    <td style="padding: 10px;">${profile.role || 'N/A'}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid #ddd;">
                    <td style="padding: 10px; font-weight: bold;">Tax Classification:</td>
                    <td style="padding: 10px;">${data.tax_classification.replace(/_/g, ' ')}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid #ddd;">
                    <td style="padding: 10px; font-weight: bold;">Address:</td>
                    <td style="padding: 10px;">${data.address_line1}${data.address_line2 ? ', ' + data.address_line2 : ''}, ${data.city}, ${data.state} ${data.zip_code}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid #ddd;">
                    <td style="padding: 10px; font-weight: bold;">Submitted:</td>
                    <td style="padding: 10px;">${new Date().toLocaleString()}</td>
                  </tr>
                </table>
                ${pdfUrl ? `
                  <div style="text-align: center; margin-top: 30px;">
                    <a href="${pdfUrl}" style="background: #002E62; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
                      Download W-9 PDF
                    </a>
                    <p style="font-size: 12px; color: #666; margin-top: 10px;">Link expires in 7 days</p>
                  </div>
                ` : ''}
                <p style="margin-top: 20px; color: #666;">
                  You can view and manage W-9 forms in the admin dashboard.
                </p>
              </div>
            </div>
          `
        );
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
        // Don't fail the submission if email fails
      }

      toast.success('W-9 submitted and PDF generated successfully!');
      setExistingW9(savedW9);
    } catch (error) {
      console.error('Error submitting W-9:', error);
      toast.error('Failed to submit W-9. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const taxIdType = watch('tax_id_type');

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6" />
          <CardTitle>W-9 Form</CardTitle>
        </div>
        <CardDescription>
          Request for Taxpayer Identification Number and Certification
        </CardDescription>
      </CardHeader>
      <CardContent>
        {existingW9 && (
          <Alert className="mb-6">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              You have a W-9 on file (submitted {new Date(existingW9.submitted_at).toLocaleDateString()}).
              You can update it below.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="legal_name">Legal Name *</Label>
              <Input
                id="legal_name"
                {...register('legal_name')}
                placeholder="As shown on your income tax return"
              />
              {errors.legal_name && (
                <p className="text-sm text-destructive">{errors.legal_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name (if different)</Label>
              <Input
                id="business_name"
                {...register('business_name')}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tax_id_type">Tax ID Type *</Label>
              <Select
                onValueChange={(value) => setValue('tax_id_type', value as 'ssn' | 'ein')}
                defaultValue={watch('tax_id_type')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssn">Social Security Number (SSN)</SelectItem>
                  <SelectItem value="ein">Employer Identification Number (EIN)</SelectItem>
                </SelectContent>
              </Select>
              {errors.tax_id_type && (
                <p className="text-sm text-destructive">{errors.tax_id_type.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_id">
                {taxIdType === 'ssn' ? 'Social Security Number' : 'Employer ID Number'} *
              </Label>
              <Input
                id="tax_id"
                {...register('tax_id')}
                placeholder={taxIdType === 'ssn' ? '123-45-6789' : '12-3456789'}
                type="password"
              />
              {errors.tax_id && (
                <p className="text-sm text-destructive">{errors.tax_id.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax_classification">Federal Tax Classification *</Label>
            <Select
              onValueChange={(value) => setValue('tax_classification', value as any)}
              defaultValue={watch('tax_classification')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select classification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual/Sole Proprietor</SelectItem>
                <SelectItem value="c_corp">C Corporation</SelectItem>
                <SelectItem value="s_corp">S Corporation</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
                <SelectItem value="trust_estate">Trust/Estate</SelectItem>
                <SelectItem value="llc_c">LLC (taxed as C Corp)</SelectItem>
                <SelectItem value="llc_s">LLC (taxed as S Corp)</SelectItem>
                <SelectItem value="llc_p">LLC (taxed as Partnership)</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.tax_classification && (
              <p className="text-sm text-destructive">{errors.tax_classification.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_line1">Address Line 1 *</Label>
            <Input
              id="address_line1"
              {...register('address_line1')}
              placeholder="Street address"
            />
            {errors.address_line1 && (
              <p className="text-sm text-destructive">{errors.address_line1.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_line2">Address Line 2</Label>
            <Input
              id="address_line2"
              {...register('address_line2')}
              placeholder="Apt, suite, etc. (optional)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input id="city" {...register('city')} />
              {errors.city && (
                <p className="text-sm text-destructive">{errors.city.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Input
                id="state"
                {...register('state')}
                placeholder="CA"
                maxLength={2}
              />
              {errors.state && (
                <p className="text-sm text-destructive">{errors.state.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="zip_code">ZIP Code *</Label>
              <Input
                id="zip_code"
                {...register('zip_code')}
                placeholder="12345"
              />
              {errors.zip_code && (
                <p className="text-sm text-destructive">{errors.zip_code.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="exempt_payee_code">Exempt Payee Code (if applicable)</Label>
              <Input
                id="exempt_payee_code"
                {...register('exempt_payee_code')}
                placeholder="Optional"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fatca_exempt_code">FATCA Exemption Code (if applicable)</Label>
              <Input
                id="fatca_exempt_code"
                {...register('fatca_exempt_code')}
                placeholder="Optional"
              />
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Under penalties of perjury, I certify that the information provided is correct and that I
              am not subject to backup withholding.
            </AlertDescription>
          </Alert>

          <div className="flex gap-4">
            <Button type="submit" disabled={isSubmitting} className="flex items-center gap-2">
              {isSubmitting ? (
                'Submitting...'
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Submit & Download W-9
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};